import React from "react";
import { landingContent } from "../../data/landingContent";

// Trending / Social Buzz section
// Shows social-media-style post cards in a marquee — honest testimonials
// styled to feel like organic social content without claiming fake metrics.


const PLATFORM_ICONS: Record<string, React.ReactElement> = {
  instagram: (
    <svg viewBox="0 0 24 24" width="14" height="14">
      <defs>
        <linearGradient id="ig-t" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FEDA75" />
          <stop offset="50%" stopColor="#D62976" />
          <stop offset="100%" stopColor="#4F5BD5" />
        </linearGradient>
      </defs>
      <rect
        x="2"
        y="2"
        width="20"
        height="20"
        rx="6"
        stroke="url(#ig-t)"
        strokeWidth="2"
        fill="none"
      />
      <circle
        cx="12"
        cy="12"
        r="4.5"
        stroke="url(#ig-t)"
        strokeWidth="2"
        fill="none"
      />
      <circle cx="17.5" cy="6.5" r="1.2" fill="url(#ig-t)" />
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="#0F172A">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.81.13v-3.5a6.37 6.37 0 00-.81-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 0010.25 4.96 6.34 6.34 0 001.88-4.51V9.4a8.16 8.16 0 004.31 1.23V7.18a4.85 4.85 0 01-1-.49z" />
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="#0F172A">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.254 5.622L18.244 2.25zM17.083 20.028h1.833L6.99 4.13H5.028L17.083 20.028z" />
    </svg>
  ),
};

function QuoteCard({
  quote,
}: {
  quote: { platform: string; user: string; text: string };
}) {
  return (
    <article className="st-trending-card">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "10px",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "0.8125rem",
              fontWeight: 800,
              color: "var(--slate-900)",
            }}
          >
            {quote.user}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              marginTop: "1px",
            }}
          >
            {PLATFORM_ICONS[quote.platform]}
            <span
              style={{
                fontSize: "0.625rem",
                fontWeight: 600,
                color: "var(--slate-400)",
                textTransform: "capitalize",
              }}
            >
              {quote.platform}
            </span>
          </div>
        </div>
      </div>
      <p
        style={{
          fontSize: "0.8125rem",
          color: "var(--slate-700)",
          lineHeight: 1.5,
          fontWeight: 500,
          margin: 0,
        }}
      >
        &ldquo;{quote.text}&rdquo;
      </p>
    </article>
  );
}

export function TrendingSection() {
  const { trending } = landingContent;
  
  const halfLength = Math.ceil(trending.quotes.length / 2);
  const row1Quotes = trending.quotes.slice(0, halfLength);
  const row2Quotes = trending.quotes.slice(halfLength);

  // Duplicate each row for seamless marquee loop
  const quotes1 = [...row1Quotes, ...row1Quotes];
  const quotes2 = [...row2Quotes, ...row2Quotes];

  return (
    <section
      className="st-section"
      style={{ background: "#FFF", overflow: "hidden", paddingBottom: "56px" }}
      aria-label="Social buzz"
    >
      <div className="st-container">
        <div
          className="st-reveal"
          style={{ textAlign: "center", marginBottom: "36px" }}
        >
          <div className="st-eyebrow" style={{ justifyContent: "center" }}>
            {trending.eyebrow}
          </div>
          <h2 className="st-heading-2">{trending.title}</h2>
          <p
            className="st-body-large"
            style={{ maxWidth: "480px", margin: "10px auto 0" }}
          >
            {trending.subtitle}
          </p>
        </div>
      </div>

      {/* Row 2 — Quote cards, reverse */}
      <div className="st-trending-row">
        <div
          className="st-reveal st-marquee-track reverse"
          style={{ display: "flex", gap: "12px", paddingBlock: "4px" }}
        >
          {quotes1.map((q, i) => (
            <QuoteCard key={i} quote={q} />
          ))}
        </div>
      </div>

      {/* Row 3 — Quote cards, forward */}
      <div className="st-trending-row">
        <div
          className="st-reveal st-marquee-track forward"
          style={{
            animationDuration: "55s",
            display: "flex",
            gap: "12px",
            paddingBlock: "4px",
          }}
        >
          {quotes2.map((q, i) => (
            <QuoteCard key={i} quote={q} />
          ))}
        </div>
      </div>
    </section>
  );
}
